import { portalApi } from "portal/state/portalApi";
import { SavedVersionMetadata } from "protos/portal/category_profile";
import { useLazyPopups } from "portal/utils/hooks/useApiPopups";
import React, {
  createContext,
  FC,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

export interface PathHighlight {
  start: string;
  between: string[];
  end: string;
  type: "parent" | "revert";
}

const PAGE_SIZE = 50;
interface PaginationState {
  loading: boolean;
  nextCursor?: string;
}
const INITIAL_PAGINATION_STATE: PaginationState = {
  loading: false,
  nextCursor: undefined,
};

interface VersionHistoryContextValue {
  currentVersionId: string;
  selectedVersionId: string;
  setSelectedVersionId: (id: string) => void;
  allLoadedVersions: SavedVersionMetadata[];
  paginationState: PaginationState;
  loadMoreVersions: () => void;
  refetchVersions: () => void;
  pathHighlight: PathHighlight | undefined;
  setPathHighlight: (highlight: PathHighlight | undefined) => void;
  onShowParent: (versionId: string, parentVersionId: string) => void;
  onShowRevert: (versionId: string, revertVersionId: string) => void;
}

const VersionHistoryContext = createContext<VersionHistoryContextValue>({
  currentVersionId: "",
  selectedVersionId: "",
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  setSelectedVersionId: () => {},
  allLoadedVersions: [],
  paginationState: INITIAL_PAGINATION_STATE,
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  loadMoreVersions: () => {},
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  refetchVersions: () => {},
  pathHighlight: undefined,
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  setPathHighlight: () => {},
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  onShowParent: () => {},
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  onShowRevert: () => {},
});

export const useVersionHistory = (): VersionHistoryContextValue =>
  useContext(VersionHistoryContext);

export type ListVersionHistory = (args: {
  uuid: string;
  limit: number;
  olderCursor?: string;
}) => Promise<{
  data?: {
    versions: SavedVersionMetadata[];
    pagination?: { nextOld?: string };
  };
  isError: boolean;
}>;
export const VersionHistoryProvider: FC<
  PropsWithChildren &
    Pick<VersionHistoryContextValue, "currentVersionId"> & {
      entityId: string;
      listVersions: ListVersionHistory;
    }
> = ({ entityId, currentVersionId, listVersions, children }) => {
  const [selectedVersionId, setSelectedVersionId] = useState(currentVersionId);
  const [allLoadedVersions, setAllLoadedVersions] = useState<
    SavedVersionMetadata[]
  >([]);
  const [paginationState, setPaginationState] = useState<PaginationState>(
    INITIAL_PAGINATION_STATE
  );

  const isFirstCall = useRef(false);

  const fetchVersions = useCallback(
    async (cursor?: string) => {
      setPaginationState((prev) => ({ ...prev, loading: true }));
      const { data, isError } = await listVersions({
        uuid: entityId,
        limit: PAGE_SIZE,
        olderCursor: cursor,
      });

      if (!isError && data?.versions) {
        setAllLoadedVersions((prev) => [...prev, ...data.versions]);
        if (isFirstCall.current && data.versions.length > 0) {
          setSelectedVersionId(data.versions[0]!.versionId);
          isFirstCall.current = false;
        }
      }
      setPaginationState({
        nextCursor: data?.pagination?.nextOld,
        loading: false,
      });
    },
    [entityId, listVersions]
  );

  const refetchVersions = useCallback(() => {
    isFirstCall.current = true;
    setAllLoadedVersions([]);
    setPaginationState(INITIAL_PAGINATION_STATE);
    fetchVersions();
  }, [fetchVersions]);

  useEffect(() => {
    refetchVersions();
  }, [entityId, refetchVersions]);

  const loadMoreVersions = useCallback(() => {
    if (!paginationState.loading && paginationState.nextCursor) {
      fetchVersions(paginationState.nextCursor);
    }
  }, [paginationState.loading, paginationState.nextCursor, fetchVersions]);

  const [pathHighlight, setPathHighlight] = useState<
    PathHighlight | undefined
  >();

  const determinePath = useCallback(
    (
      startVersion: string,
      endVersion: string
    ): Omit<PathHighlight, "type"> | undefined => {
      const between: string[] = [];
      let isBetween = false;
      let foundEnd = false;
      for (const { versionId: cvId } of allLoadedVersions) {
        const isStart = cvId === startVersion;
        const isEnd = cvId === endVersion;
        if (isBetween && !isEnd) {
          between.push(cvId);
        }
        if (isStart) {
          isBetween = true;
        }
        if (isEnd) {
          isBetween = false;
          foundEnd = true;
        }
      }
      return foundEnd
        ? {
            start: startVersion,
            between,
            end: endVersion,
          }
        : undefined;
    },
    [allLoadedVersions]
  );

  const onShowParent = useCallback(
    (versionId: string, parentVersionId: string) => {
      const path = determinePath(versionId, parentVersionId);
      setPathHighlight((prev) =>
        (prev?.type === "parent" && prev.start === versionId) || !path
          ? undefined
          : {
              ...path,
              type: "parent",
            }
      );
    },
    [determinePath]
  );

  const onShowRevert = useCallback(
    (versionId: string, revertVersionId: string) => {
      const path = determinePath(versionId, revertVersionId);
      if (!path) {
        setPathHighlight(undefined);
      }
      setPathHighlight((prev) =>
        (prev?.type === "revert" && prev.start === versionId) || !path
          ? undefined
          : {
              ...path,
              type: "revert",
            }
      );
    },
    [determinePath]
  );

  const value: VersionHistoryContextValue = useMemo(
    () => ({
      currentVersionId,
      selectedVersionId,
      setSelectedVersionId,
      allLoadedVersions,
      paginationState,
      loadMoreVersions,
      refetchVersions,
      pathHighlight,
      setPathHighlight,
      onShowParent,
      onShowRevert,
    }),
    [
      currentVersionId,
      selectedVersionId,
      allLoadedVersions,
      paginationState,
      loadMoreVersions,
      refetchVersions,
      pathHighlight,
      setPathHighlight,
      onShowParent,
      onShowRevert,
    ]
  );

  return (
    <VersionHistoryContext.Provider value={value}>
      {children}
    </VersionHistoryContext.Provider>
  );
};

export const PlantProfileHistoryProvider: FC<
  PropsWithChildren &
    Pick<VersionHistoryContextValue, "currentVersionId"> & {
      plantProfileId: string;
    }
> = ({ plantProfileId, currentVersionId, children }) => {
  const [getVersions] = useLazyPopups(
    portalApi.endpoints.listPlantProfileVersions.useLazyQuery()
  );
  return (
    <VersionHistoryProvider
      entityId={plantProfileId}
      currentVersionId={currentVersionId}
      listVersions={getVersions}
    >
      {children}
    </VersionHistoryProvider>
  );
};

export const CategoryTemplateHistoryProvider: FC<
  PropsWithChildren &
    Pick<VersionHistoryContextValue, "currentVersionId"> & {
      categoryId: string;
    }
> = ({ categoryId, currentVersionId, children }) => {
  const [getVersions] = useLazyPopups(
    portalApi.endpoints.listCategoryVersions.useLazyQuery()
  );
  return (
    <VersionHistoryProvider
      entityId={categoryId}
      currentVersionId={currentVersionId}
      listVersions={getVersions}
    >
      {children}
    </VersionHistoryProvider>
  );
};
